import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Box, Container, Typography, TextField, IconButton, Paper, Avatar, Button, CircularProgress, useTheme, Card, CardContent, Chip } from '@mui/material';
import { Send as SendIcon, Mic as MicIcon, AttachFile as AttachFileIcon, Delete as DeleteIcon, PersonAdd as PersonAddIcon, CheckCircle as CheckCircleIcon, Phone as PhoneIcon, Email as EmailIcon, Message as MessageIcon, SmartToy as SmartToyIcon, Search as SearchIcon, Campaign as CampaignIcon, Assessment as AssessmentIcon } from '@mui/icons-material';
import { publicChatApi, PublicChatMessage } from '../utils/publicChatApi';
import { extractMediaFromContent, splitMessageContent } from '../utils/messageUtils';
import { WhatsApp as WhatsAppIcon } from '@mui/icons-material';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  media?: Array<{ type: 'image' | 'video'; url: string; alt?: string }>;
}

const PublicDemoPage: React.FC = () => {
  const theme = useTheme();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showLeadSuggestion, setShowLeadSuggestion] = useState(false);
  const [showLeadCreation, setShowLeadCreation] = useState(false);
  const [leadCreated, setLeadCreated] = useState(false);
  const [showWhatsAppMessage, setShowWhatsAppMessage] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const leadCreationRef = useRef<HTMLDivElement>(null);
  const chatHistoryRef = useRef<PublicChatMessage[]>([]);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const isInitialMount = useRef(true);

  const examplePrompts = [
    'Olá, boa tarde! Gostaria de tirar algumas dúvidas',
    'Gostaria de fazer um agendamento',
    'Quais são os valores?',
    'Gostaria de saber mais informações',
    'Qual o horário de funcionamento?',
    'Encontrei vocês no Instagram, gostaria de saber mais',
  ];

  // Garantir que a página comece no topo ao carregar
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Não fazer scroll no carregamento inicial
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    // Só fazer scroll se houver mensagens e dentro do container do chat
    if (messages.length > 0 && messagesContainerRef.current && messagesEndRef.current) {
      // Fazer scroll apenas dentro do container de mensagens, não na página inteira
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [messages]);

  useEffect(() => {
    // Mostrar card de sugestão após 5 mensagens no total
    if (messages.length >= 5 && !showLeadSuggestion && !showLeadCreation) {
      setShowLeadSuggestion(true);
    }
  }, [messages.length, showLeadSuggestion, showLeadCreation]);

  // Gerar resumo da conversa
  const generateConversationSummary = (): string => {
    const userMessages = messages
      .filter(msg => msg.role === 'user')
      .map(msg => msg.content.replace('🎤 ', ''))
      .slice(0, 5);
    
    if (userMessages.length === 0) {
      return 'Cliente demonstrou interesse em conhecer mais sobre nossos serviços.';
    }
    
    return userMessages.join('; ') + '.';
  };

  // Simular dados do lead (usando useMemo para recalcular quando messages mudar)
  const simulatedLead = useMemo(() => ({
    name: 'João Silva',
    phone: '(11) 98765-4321',
    email: 'joao.silva@email.com',
    summary: generateConversationSummary(),
  }), [messages]);

  const handleShowLeadCreation = () => {
    setShowLeadSuggestion(false);
    setShowLeadCreation(true);
    setLeadCreated(false);
    setShowWhatsAppMessage(false);
    
    // Scroll para a seção de criação de lead após um pequeno delay
    setTimeout(() => {
      leadCreationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      
      // Simular criação do lead após 2 segundos
      setTimeout(() => {
        setLeadCreated(true);
        
        // Mostrar mensagem WhatsApp após 1 segundo
        setTimeout(() => {
          setShowWhatsAppMessage(true);
        }, 1000);
      }, 2000);
    }, 100);
  };

  const handleSend = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || sending) return;

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: trimmed,
      timestamp: Date.now(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setSending(true);
    setError(null);

    try {
      const result = await publicChatApi.sendMessage({
        message: trimmed,
        chatHistory: chatHistoryRef.current,
      });

      chatHistoryRef.current = result.updatedChatHistory;

      // Processar resposta da IA para extrair mídia
      const processedParts = splitMessageContent(result.response);
      
      processedParts.forEach((part, index) => {
        const assistantMessage: ChatMessage = {
          id: `assistant-${Date.now()}-${index}`,
          role: 'assistant',
          content: part.text,
          timestamp: Date.now(),
          media: part.media,
        };
        setMessages((prev) => [...prev, assistantMessage]);
      });
    } catch (err: any) {
      setError(err.message || 'Erro ao enviar mensagem');
      console.error('Erro ao enviar mensagem:', err);
      setMessages((prev) => prev.filter(msg => msg.id !== userMessage.id));
    } finally {
      setSending(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Detectar o melhor tipo MIME suportado
      let mimeType = 'audio/webm';
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/webm';
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
      } else if (MediaRecorder.isTypeSupported('audio/ogg')) {
        mimeType = 'audio/ogg';
      }
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const blobType = mimeType.split(';')[0]; // Remove codecs parameter
        const audioBlob = new Blob(audioChunksRef.current, { type: blobType });
        await handleSendAudio(audioBlob, blobType);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setRecording(true);
    } catch (err) {
      console.error('Erro ao iniciar gravação:', err);
      setError('Erro ao acessar o microfone. Verifique as permissões.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const handleSendAudio = async (audioBlob: Blob, mimeType: string = 'audio/webm') => {
    setSending(true);
    setError(null);

    try {
      // Determinar extensão baseada no tipo MIME
      let extension = 'webm';
      if (mimeType.includes('mp4')) extension = 'mp4';
      else if (mimeType.includes('ogg')) extension = 'ogg';
      else if (mimeType.includes('wav')) extension = 'wav';
      
      const audioFile = new File([audioBlob], `audio.${extension}`, { type: mimeType });
      const result = await publicChatApi.sendAudio(audioFile, chatHistoryRef.current);

      // Encontrar a mensagem do usuário transcrita (penúltima mensagem antes da resposta da IA)
      const userTranscribedMessage = result.updatedChatHistory[result.updatedChatHistory.length - 2];
      
      chatHistoryRef.current = result.updatedChatHistory;

      // Adicionar mensagem de áudio do usuário (transcrita)
      if (userTranscribedMessage && userTranscribedMessage.role === 'user') {
        const userMessage: ChatMessage = {
          id: `user-audio-${Date.now()}`,
          role: 'user',
          content: `🎤 ${userTranscribedMessage.content}`,
          timestamp: Date.now(),
        };
        setMessages((prev) => [...prev, userMessage]);
      }

      // Processar resposta da IA
      const processedParts = splitMessageContent(result.response);
      
      processedParts.forEach((part, index) => {
        const assistantMessage: ChatMessage = {
          id: `assistant-${Date.now()}-${index}`,
          role: 'assistant',
          content: part.text,
          timestamp: Date.now(),
          media: part.media,
        };
        setMessages((prev) => [...prev, assistantMessage]);
      });
    } catch (err: any) {
      setError(err.message || 'Erro ao enviar áudio');
      console.error('Erro ao enviar áudio:', err);
    } finally {
      setSending(false);
    }
  };

  const handleClearChat = async () => {
    try {
      await publicChatApi.clearChatHistory();
      setMessages([]);
      chatHistoryRef.current = [];
      setError(null);
      setShowLeadSuggestion(false);
      setShowLeadCreation(false);
      setLeadCreated(false);
      setShowWhatsAppMessage(false);
    } catch (err) {
      console.error('Erro ao limpar chat:', err);
      setError('Erro ao limpar o chat');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleExampleClick = async (exampleText: string) => {
    if (sending) return;

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: exampleText,
      timestamp: Date.now(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setSending(true);
    setError(null);

    try {
      const result = await publicChatApi.sendMessage({
        message: exampleText,
        chatHistory: chatHistoryRef.current,
      });

      chatHistoryRef.current = result.updatedChatHistory;

      // Processar resposta da IA para extrair mídia
      const processedParts = splitMessageContent(result.response);
      
      processedParts.forEach((part, index) => {
        const assistantMessage: ChatMessage = {
          id: `assistant-${Date.now()}-${index}`,
          role: 'assistant',
          content: part.text,
          timestamp: Date.now(),
          media: part.media,
        };
        setMessages((prev) => [...prev, assistantMessage]);
      });
    } catch (err: any) {
      setError(err.message || 'Erro ao enviar mensagem');
      console.error('Erro ao enviar mensagem:', err);
      setMessages((prev) => prev.filter(msg => msg.id !== userMessage.id));
    } finally {
      setSending(false);
    }
  };


  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: '#313131',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          padding: 2,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Box
            component="img"
            src="/logo-branco.svg"
            alt="Lia Chat"
            sx={{
              height: 60,
              width: 'auto',
              mb: 2,
              objectFit: 'contain',
            }}
          />
          <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
            Converse com nossa IA
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
            Experimente nossa inteligência artificial em uma interface similar ao WhatsApp
          </Typography>
        </Container>
      </Box>

      {/* Chat Container */}
      <Container maxWidth="md" sx={{ flex: 1, display: 'flex', flexDirection: 'column', py: 3 }}>
        <Paper
          elevation={24}
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 3,
            overflow: 'hidden',
            background: '#e5ddd5',
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cdefs%3E%3Cpattern id=\'grid\' width=\'100\' height=\'100\' patternUnits=\'userSpaceOnUse\'%3E%3Cpath d=\'M 100 0 L 0 0 0 100\' fill=\'none\' stroke=\'%23f0f0f0\' stroke-width=\'1\'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width=\'100\' height=\'100\' fill=\'url(%23grid)\' /%3E%3C/svg%3E")',
          }}
        >
          {/* Chat Header */}
          <Box
            sx={{
              background: '#075e54',
              color: 'white',
              padding: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar 
                src="/logo-lia-chat.png"
                sx={{ bgcolor: '#128c7e', width: 40, height: 40 }}
              />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  Assistente IA
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Online
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={handleClearChat}
              sx={{ color: 'white' }}
              title="Limpar chat"
            >
              <DeleteIcon />
            </IconButton>
          </Box>

          {/* Messages Area */}
          <Box
            ref={messagesContainerRef}
            sx={{
              flex: 1,
              overflowY: 'auto',
              padding: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            {messages.length === 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 1,
                  gap: 3,
                  px: 2,
                  py: 4,
                }}
              >
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <Avatar 
                    src="/logo-lia-chat.png"
                    sx={{ 
                      bgcolor: '#128c7e', 
                      width: 64, 
                      height: 64,
                      mx: 'auto',
                      mb: 2,
                    }}
                  />
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      color: '#000',
                      fontWeight: 'bold',
                      mb: 1,
                    }}
                  >
                    👋 Olá! Sou sua assistente de IA
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: 'rgba(0,0,0,0.6)',
                    }}
                  >
                    Como posso ajudar você hoje?
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 2,
                    width: '100%',
                    maxWidth: '600px',
                  }}
                >
                  {examplePrompts.map((prompt, index) => (
                    <Paper
                      key={index}
                      elevation={2}
                      onClick={() => handleExampleClick(prompt)}
                      sx={{
                        padding: 2,
                        cursor: 'pointer',
                        background: 'white',
                        borderRadius: '12px',
                        border: '1px solid rgba(0,0,0,0.1)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          borderColor: '#128c7e',
                        },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#303030',
                          fontWeight: 500,
                        }}
                      >
                        {prompt}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              </Box>
            )}

            {messages.map((message) => (
              <Box
                key={message.id}
                sx={{
                  display: 'flex',
                  justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                  alignItems: 'flex-start',
                  gap: 1,
                }}
              >
                {message.role === 'assistant' && (
                  <Avatar 
                    src="/logo-lia-chat.png"
                    sx={{ bgcolor: '#128c7e', width: 32, height: 32 }}
                  />
                )}
                <Box
                  sx={{
                    maxWidth: '70%',
                    background: message.role === 'user' ? '#dcf8c6' : 'white',
                    borderRadius: '7.5px',
                    padding: 1.5,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                    position: 'relative',
                    '&::before': message.role === 'user' ? {
                      content: '""',
                      position: 'absolute',
                      right: -8,
                      top: 0,
                      width: 0,
                      height: 0,
                      borderTop: '8px solid transparent',
                      borderBottom: '8px solid transparent',
                      borderLeft: `8px solid #dcf8c6`,
                    } : {
                      content: '""',
                      position: 'absolute',
                      left: -8,
                      top: 0,
                      width: 0,
                      height: 0,
                      borderTop: '8px solid transparent',
                      borderBottom: '8px solid transparent',
                      borderRight: '8px solid white',
                    },
                  }}
                >
                  {/* Media Content */}
                  {message.media && message.media.length > 0 && (
                    <Box sx={{ mb: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {message.media.map((media, idx) => (
                        <Box key={idx}>
                          {media.type === 'image' ? (
                            <Box
                              component="img"
                              src={media.url}
                              alt={media.alt || 'Image'}
                              sx={{
                                maxWidth: '100%',
                                maxHeight: '300px',
                                borderRadius: '7.5px',
                                objectFit: 'cover',
                                cursor: 'pointer',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                '&:hover': {
                                  opacity: 0.9,
                                },
                              }}
                              onClick={() => window.open(media.url, '_blank')}
                            />
                          ) : (
                            <Box
                              component="video"
                              src={media.url}
                              controls
                              sx={{
                                maxWidth: '100%',
                                maxHeight: '300px',
                                borderRadius: '7.5px',
                              }}
                            />
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Text Content */}
                  {message.content && (
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        color: '#303030',
                      }}
                    >
                      {message.content}
                    </Typography>
                  )}

                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      textAlign: 'right',
                      mt: 0.5,
                      color: 'rgba(0,0,0,0.5)',
                      fontSize: '0.7rem',
                    }}
                  >
                    {new Date(message.timestamp).toLocaleTimeString('pt-BR', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Typography>
                </Box>
                {message.role === 'user' && (
                  <Avatar sx={{ bgcolor: '#128c7e', width: 32, height: 32 }}>
                    👤
                  </Avatar>
                )}
              </Box>
            ))}

            {error && (
              <Box
                sx={{
                  background: '#ffebee',
                  color: '#c62828',
                  padding: 1.5,
                  borderRadius: '7.5px',
                  textAlign: 'center',
                }}
              >
                <Typography variant="body2">{error}</Typography>
              </Box>
            )}

            {sending && messages.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Avatar 
                  src="/logo-lia-chat.png"
                  sx={{ bgcolor: '#128c7e', width: 32, height: 32 }}
                />
                <Box
                  sx={{
                    background: 'white',
                    borderRadius: '7.5px',
                    padding: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <CircularProgress size={16} />
                  <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.5)' }}>
                    Digitando...
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Card de sugestão para criar lead */}
            {showLeadSuggestion && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  my: 2,
                }}
              >
                <Card
                  sx={{
                    maxWidth: '400px',
                    background: 'linear-gradient(135deg, #128c7e 0%, #075e54 100%)',
                    color: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <PersonAddIcon sx={{ fontSize: 28 }} />
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Criar Lead
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 2, opacity: 0.95 }}>
                      Veja como seria a criação automática de um lead a partir desta conversa e o envio do resumo para o vendedor.
                    </Typography>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleShowLeadCreation}
                      sx={{
                        background: 'white',
                        color: '#128c7e',
                        fontWeight: 'bold',
                        textTransform: 'none',
                        borderRadius: '8px',
                        py: 1,
                        '&:hover': {
                          background: 'rgba(255, 255, 255, 0.9)',
                        },
                      }}
                    >
                      Ver Demonstração
                    </Button>
                  </CardContent>
                </Card>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Box>

          {/* Input Area */}
          <Box
            sx={{
              background: '#f0f0f0',
              padding: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              borderTop: '1px solid rgba(0,0,0,0.1)',
            }}
          >
            <IconButton
              onClick={recording ? stopRecording : startRecording}
              disabled={sending}
              sx={{
                color: recording ? '#c62828' : '#128c7e',
                '&:hover': {
                  background: 'rgba(18, 140, 126, 0.1)',
                },
              }}
            >
              <MicIcon />
            </IconButton>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Digite uma mensagem..."
              disabled={sending}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '25px',
                  background: 'white',
                  color: '#000',
                  '& fieldset': {
                    borderColor: 'rgba(0,0,0,0.1)',
                  },
                  '& input': {
                    color: '#000',
                  },
                  '& textarea': {
                    color: '#000',
                  },
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'rgba(0,0,0,0.5)',
                  opacity: 1,
                },
              }}
            />
            <IconButton
              onClick={handleSend}
              disabled={!inputValue.trim() || sending}
              sx={{
                color: '#128c7e',
                '&:hover': {
                  background: 'rgba(18, 140, 126, 0.1)',
                },
                '&:disabled': {
                  color: 'rgba(0,0,0,0.3)',
                },
              }}
            >
              {sending ? <CircularProgress size={24} /> : <SendIcon />}
            </IconButton>
          </Box>
        </Paper>
      </Container>

      {/* Seção de Criação de Lead */}
      {showLeadCreation && (
        <Box
          ref={leadCreationRef}
          sx={{
            background: '#313131',
            py: 6,
            minHeight: '100vh',
          }}
        >
          <Container maxWidth="md">
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold', mb: 2 }}>
                Criação Automática de Lead
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                Veja como o sistema cria automaticamente um lead e envia o resumo para o vendedor
              </Typography>
            </Box>

            <Paper
              elevation={24}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                background: 'white',
              }}
            >
              {/* Header do Lead */}
              <Box
                sx={{
                  background: '#1a1a1a',
                  color: 'white',
                  padding: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                {!leadCreated ? (
                  <>
                    <CircularProgress size={40} sx={{ color: 'white' }} />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Criando lead...
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Processando informações da conversa
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <>
                    <CheckCircleIcon sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Lead criado com sucesso!
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Resumo enviado para o vendedor
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>

              {/* Conteúdo do Lead */}
              <Box sx={{ p: 4 }}>
                {!leadCreated ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress size={60} sx={{ mb: 3 }} />
                    <Typography variant="body1" sx={{ color: 'rgba(0,0,0,0.6)' }}>
                      Processando dados da conversa...
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    {/* Simulação de Mensagem WhatsApp */}
                    {showWhatsAppMessage && (
                      <Box 
                        sx={{ 
                          mb: 4,
                          animation: 'fadeIn 0.5s ease-in',
                          '@keyframes fadeIn': {
                            from: { opacity: 0, transform: 'translateY(-10px)' },
                            to: { opacity: 1, transform: 'translateY(0)' },
                          },
                        }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#303030' }}>
                          📱 Mensagem enviada para o vendedor
                        </Typography>
                        
                        <Paper
                          elevation={2}
                          sx={{
                            background: '#e5ddd5',
                            borderRadius: 2,
                            p: 2,
                            maxWidth: '500px',
                            mx: 'auto',
                            animation: 'slideIn 0.6s ease-out',
                            '@keyframes slideIn': {
                              from: { opacity: 0, transform: 'translateX(-20px)' },
                              to: { opacity: 1, transform: 'translateX(0)' },
                            },
                          }}
                        >
                          {/* Header estilo WhatsApp */}
                          <Box
                            sx={{
                              background: '#075e54',
                              color: 'white',
                              padding: 1.5,
                              borderRadius: '8px 8px 0 0',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                            }}
                          >
                            <Avatar sx={{ bgcolor: '#128c7e', width: 40, height: 40 }}>
                              👤
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                Vendedor
                              </Typography>
                              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                Online
                              </Typography>
                            </Box>
                          </Box>

                          {/* Mensagem */}
                          <Box
                            sx={{
                              background: '#dcf8c6',
                              borderRadius: '0 0 8px 8px',
                              padding: 2,
                              mt: 0.5,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#303030',
                                whiteSpace: 'pre-wrap',
                                mb: 1,
                              }}
                            >
                              🎯 *Novo Lead Criado!*
                              
                              📋 *Informações do Lead:*
                              
                              👤 *Nome:* {simulatedLead.name}
                              📞 *Telefone:* {simulatedLead.phone}
                              📧 *E-mail:* {simulatedLead.email}
                              
                              💬 *Resumo da Conversa:*
                              {simulatedLead.summary}
                              
                              ⏰ Lead criado em {new Date().toLocaleString('pt-BR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                textAlign: 'right',
                                color: 'rgba(0,0,0,0.5)',
                                fontSize: '0.7rem',
                              }}
                            >
                              {new Date().toLocaleTimeString('pt-BR', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </Typography>
                          </Box>
                        </Paper>
                      </Box>
                    )}

                    {/* Card Completo do Lead */}
                    {showWhatsAppMessage && (
                      <Box
                        sx={{
                          animation: 'fadeInUp 0.8s ease-out 0.3s both',
                          '@keyframes fadeInUp': {
                            from: { opacity: 0, transform: 'translateY(20px)' },
                            to: { opacity: 1, transform: 'translateY(0)' },
                          },
                        }}
                      >
                        <Box sx={{ mb: 4 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3, color: '#303030' }}>
                        Informações do Lead
                      </Typography>
                      
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: '#128c7e', width: 48, height: 48 }}>
                            <PersonAddIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.6)', display: 'block', mb: 0.5 }}>
                              Nome
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#303030' }}>
                              {simulatedLead.name}
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: '#128c7e', width: 48, height: 48 }}>
                            <PhoneIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.6)', display: 'block', mb: 0.5 }}>
                              Telefone
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#303030' }}>
                              {simulatedLead.phone}
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: '#128c7e', width: 48, height: 48 }}>
                            <EmailIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.6)', display: 'block', mb: 0.5 }}>
                              E-mail
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#303030' }}>
                              {simulatedLead.email}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>

                    <Box sx={{ 
                      background: '#f5f5f5', 
                      borderRadius: 2, 
                      p: 3,
                      border: '1px solid rgba(0,0,0,0.1)',
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <MessageIcon sx={{ color: '#128c7e' }} />
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#303030' }}>
                          Resumo da Conversa
                        </Typography>
                      </Box>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#303030',
                          lineHeight: 1.8,
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {simulatedLead.summary}
                      </Typography>
                    </Box>

                    <Box sx={{ mt: 4, p: 3, background: '#e8f5e9', borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                        <CheckCircleIcon sx={{ color: '#4caf50' }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                          Notificação enviada
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: '#2e7d32', pl: 4.5 }}>
                        O vendedor foi notificado sobre este novo lead e recebeu todas as informações da conversa.
                      </Typography>
                    </Box>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Paper>
          </Container>
        </Box>
      )}

      {/* Seções de Serviços */}
      <Box
        sx={{
          background: '#464646',
          py: 8,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" sx={{ color: 'white', fontWeight: 'bold', mb: 2 }}>
              Soluções Completas de Automação
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', maxWidth: '800px', mx: 'auto' }}>
              Descubra como nossas soluções podem transformar seu negócio com automação inteligente
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 6,
            }}
          >
            {/* Serviço 1: IA com Atendimento no WhatsApp */}
            <Box>
              <Paper
                elevation={8}
                sx={{
                  borderRadius: 3,
                  overflow: 'hidden',
                  background: 'white',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  sx={{
                    background: '#1a1a1a',
                    color: 'white',
                    padding: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <WhatsAppIcon sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      IA com Atendimento no WhatsApp
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Crie agentes de IA e conecte com qualquer sistema
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body1" sx={{ mb: 3, color: '#303030', lineHeight: 1.8 }}>
                    Nossa solução de IA para WhatsApp permite criar agentes inteligentes que atendem seus clientes 24/7, integrando-se com qualquer sistema existente. Com CRM completo integrado, você gerencia todos os leads em um só lugar, aumentando a produtividade e melhorando a experiência do cliente.
                  </Typography>

                  <Box
                    sx={{
                      position: 'relative',
                      borderRadius: 2,
                      overflow: 'hidden',
                      mb: 2,
                      background: '#000',
                    }}
                  >
                    <Box
                      component="video"
                      src="/videos/lia-talk.mov"
                      autoPlay
                      loop
                      muted
                      playsInline
                      controls
                      sx={{
                        width: '100%',
                        height: 'auto',
                        display: 'block',
                        maxHeight: '300px',
                        objectFit: 'cover',
                      }}
                    />
                  </Box>
                </Box>
              </Paper>
            </Box>

            {/* Serviço 2: Busca de Leads no Google e Disparo */}
            <Box>
              <Paper
                elevation={8}
                sx={{
                  borderRadius: 3,
                  overflow: 'hidden',
                  background: 'white',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  sx={{
                    background: '#1a1a1a',
                    color: 'white',
                    padding: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <SearchIcon sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      Busca de Leads no Google e Disparo
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Buscas automáticas e disparo inteligente
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body1" sx={{ mb: 3, color: '#303030', lineHeight: 1.8 }}>
                    Automatize a busca de empresas no Google Search e Google Maps. Nossa ferramenta encontra leads qualificados automaticamente e realiza disparos inteligentes via WhatsApp (oficial e não oficial) e Email. Ideal para empresas que querem expandir sua base de contatos de forma eficiente.
                  </Typography>

                  <Box
                    sx={{
                      position: 'relative',
                      borderRadius: 2,
                      overflow: 'hidden',
                      mb: 2,
                      background: '#000',
                    }}
                  >
                    <Box
                      component="video"
                      src="/videos/lia-hunter.mov"
                      autoPlay
                      loop
                      muted
                      playsInline
                      controls
                      sx={{
                        width: '100%',
                        height: 'auto',
                        display: 'block',
                        maxHeight: '300px',
                        objectFit: 'cover',
                      }}
                    />
                  </Box>
                </Box>
              </Paper>
            </Box>

            {/* Serviço 3: Disparo em Massa por WhatsApp */}
            <Box>
              <Paper
                elevation={8}
                sx={{
                  borderRadius: 3,
                  overflow: 'hidden',
                  background: 'white',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  sx={{
                    background: '#1a1a1a',
                    color: 'white',
                    padding: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <CampaignIcon sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      Disparo em Massa por WhatsApp
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Campanhas em massa via WhatsApp
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body1" sx={{ mb: 3, color: '#303030', lineHeight: 1.8 }}>
                    Realize campanhas de marketing em massa através do WhatsApp. Importe planilhas Excel ou integre via API.  Solução perfeita para empresas que precisam alcançar muitos clientes rapidamente.
                  </Typography>

                  <Box
                    sx={{
                      position: 'relative',
                      borderRadius: 2,
                      overflow: 'hidden',
                      mb: 2,
                      background: '#000',
                    }}
                  >
                    <Box
                      component="video"
                      src="/videos/lia-send.mov"
                      autoPlay
                      loop
                      muted
                      playsInline
                      controls
                      sx={{
                        width: '100%',
                        height: 'auto',
                        display: 'block',
                        maxHeight: '300px',
                        objectFit: 'cover',
                      }}
                    />
                  </Box>
                </Box>
              </Paper>
            </Box>

            {/* Serviço 4: Análise de Poder de Compra do Lead */}
            <Box>
              <Paper
                elevation={8}
                sx={{
                  borderRadius: 3,
                  overflow: 'hidden',
                  background: 'white',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  sx={{
                    background: '#1a1a1a',
                    color: 'white',
                    padding: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <AssessmentIcon sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      Análise de Poder de Compra do Lead
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Descubra o potencial real de compra
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body1" sx={{ mb: 3, color: '#303030', lineHeight: 1.8 }}>
                    Descubra o potencial real de compra dos seus leads através de análise inteligente. Nossa ferramenta analisa salários, veículos, score de crédito e muito mais, ajudando você a priorizar os melhores prospects e aumentar significativamente sua taxa de conversão.
                  </Typography>

                  <Box
                    sx={{
                      position: 'relative',
                      borderRadius: 2,
                      overflow: 'hidden',
                      mb: 2,
                      background: '#000',
                    }}
                  >
                    <Box
                      component="video"
                      src="/videos/lia-rating.mov"
                      autoPlay
                      loop
                      muted
                      playsInline
                      controls
                      sx={{
                        width: '100%',
                        height: 'auto',
                        display: 'block',
                        maxHeight: '300px',
                        objectFit: 'cover',
                      }}
                    />
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          background: '#1a1a1a',
          color: 'white',
          py: 4,
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 3,
            }}
          >
            {/* Logo e Copyright */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: { xs: 'center', md: 'flex-start' },
                gap: 1,
              }}
            >
              <Box
                component="img"
                src="/logo-branco.svg"
                alt="Lia Talk"
                sx={{
                  height: 40,
                  width: 'auto',
                  objectFit: 'contain',
                }}
              />
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                © {new Date().getFullYear()} Lia Talk. Todos os direitos reservados.
              </Typography>
            </Box>

            {/* Links Sociais */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 3,
                flexWrap: 'wrap',
                justifyContent: { xs: 'center', md: 'flex-end' },
              }}
            >
              <Box
                component="a"
                href="https://liatalk.ai/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  transition: 'color 0.2s ease',
                  '&:hover': {
                    color: '#128c7e',
                  },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Site
                </Typography>
              </Box>

              <Box
                component="a"
                href="https://www.instagram.com/liatalk.ai/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  transition: 'color 0.2s ease',
                  '&:hover': {
                    color: '#E4405F',
                  },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Instagram
                </Typography>
              </Box>

              <Box
                component="a"
                href="https://www.youtube.com/@liatalk.ai"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  transition: 'color 0.2s ease',
                  '&:hover': {
                    color: '#FF0000',
                  },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  YouTube
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default PublicDemoPage;
